import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Drawer } from '@mui/material';
import PropTypes from 'prop-types';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import ComponentPaneHeader from './ComponentPaneHeader';
import styles from './ComponentPaneStyle';
import { withStyles } from '@mui/styles';
import useProjectStore from '../../ProjectCanvas/useProjectStore';
import useCanvasStore from '../Canvas/useCanvasStore';
//sending requests
import { send_request } from '../../../utils/Request';
import { LEGACY_FORM_TYPES, isLegacyForm } from '../../../utils/formTypeConstants';
import { saveComponentByType } from '../../../utils/buildComponentSavePayload';
import { storeMap } from '../../../utils/prefillComponentStores';
//gets component template by type
import getTemplate from './TemplateDetector';
//placeholder for loading
import { IndividualComponentLoader } from '../../../utils/ComponentLoadingSkeleton';
import config from '../../../config';
import { useNavigate } from 'react-router-dom';
import { CanvasContext } from '../Canvas/WorkflowCanvas';
import {
  DEFAULT_ZOOM_FOR_FOCUSING_VIEWPORT,
  setPositionInCanvasView,
} from '../../../utils/layoutUtils';
import { keyframes, styled } from '@mui/material/styles';
import { Button, Grid } from '@mui/material';
import { Settings } from '@mui/icons-material';
import FormAdditionalButtons from '../../FormBuilder/FormComponents/FormAdditionalButtons';
import FormPreviewModal from './FormPreviewModal';
import { ComponentMetadataSettingsDialog } from '../../Component/Dialog/ComponentMetadataSettingsDialog';
import { computePlaceholdersForComponent } from '../../../utils/placeholderExtractor';
import {
  computePlaceholdersForComponentFromOutputMap,
  mergePlaceholderResponses,
} from '../../../utils/placeholderParity';
import FormBuilderContainer from '../../FormBuilder/FormBuilderContainer';
import SequentialFormContainer from '../../SequentialForm/SequentialFormContainer';
import { getAPIResReqStore } from '../../APIRequestAndResponse/useAPIResReqStore';

//================================================================= Styled Components =========================================

// Define fade-in animation
const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

// Replace with modern animations
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;


const ReadOnlyBanner = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.action.disabledBackground,
  display: 'inline-flex',
  borderRadius: '0 0  4px 4px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 24px',
  fontSize: '14px',
  color: theme.palette.text.primary,
  position: 'sticky',
  top: 0,
  zIndex: 1002,
  margin: '0px auto 0px',
  left: 0,
  right: 0,
  animation: `${fadeInUp} 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
}));

const TemplateContainer = styled('div')(() => ({
  padding: '16px',
}));

// Styled text button for component settings (matches FormAdditionalButtons style)
const TextButton = styled(Button)(({ theme }) => ({
  textTransform: 'unset !important',
  color: theme.palette.text.primary,
}));

export const FormContext = createContext([]);

// clearPlaceholderCache kept as a no-op export for backwards compatibility
// with callers that still reference it (e.g. canvas save handlers).
export function clearPlaceholderCache() {}

const FORM_TYPES = LEGACY_FORM_TYPES;

const SEQUENTIAL_FORM_SNAPSHOT_KEYS = [
  'pages', 'pageTitles', 'message', 'notificationMethod', 'email', 'emailSubject',
  'users', 'dueDateStatus', 'dueDatePeriod', 'dueDateOn', 'reminderUsers',
  'reminderEmailMessage', 'reminderEmail', 'reminderCcEmails', 'reminderReplyToEmails',
  'reminderEmailSubject', 'reminderInterval', 'reminderType', 'reminderTimezone',
  'reminderFrequency', 'reminder', 'reminderLimit', 'reminderOption',
  'reminderOnPlaceholderValue', 'toDoColor', 'toDoLabel', 'completedColor',
  'completedLabel', 'credentialId', 'columnId', 'databaseId', 'ccEmails', 'replyToEmails',
];

function deepClone(obj) {
  if (obj === undefined) return undefined;
  return JSON.parse(JSON.stringify(obj));
}

function getFormContainer(componentType) {
  if (componentType === 'form') return FormBuilderContainer;
  if (componentType === 'sequential_form' || componentType === 'form_section') return SequentialFormContainer;
  return null;
}

function captureSnapshot(componentId, componentType) {
  const snapshot = {
    componentId,
    componentType,
    storeState: null,
    formContainerState: null,
    draggingNodeData: null,
    projectStoreSlice: null,
    projectComponentEntry: null,
  };

  // 1. Capture keyed Zustand store state (or form container state)
  if (FORM_TYPES.includes(componentType)) {
    const container = getFormContainer(componentType);
    if (container) {
      if (componentType === 'form') {
        snapshot.formContainerState = {
          pages: deepClone(container.pages),
          pageTitles: deepClone(container.pageTitles),
        };
      } else {
        const state = {};
        for (const key of SEQUENTIAL_FORM_SNAPSHOT_KEYS) {
          state[key] = deepClone(container[key]);
        }
        snapshot.formContainerState = state;
      }
    }
  } else if (componentType === 'api_request_and_response') {
    const store = getAPIResReqStore(componentId);
    const state = store.getState();
    const { loaded, endpoint, ...data } = state;
    const serializable = {};
    for (const [k, v] of Object.entries(data)) {
      if (typeof v !== 'function') serializable[k] = deepClone(v);
    }
    snapshot.storeState = serializable;
  } else {
    const config = storeMap[componentType];
    if (config) {
      const store = config.getStore(componentId);
      const state = store.getState();
      const serializable = {};
      for (const [k, v] of Object.entries(state)) {
        if (typeof v !== 'function') serializable[k] = deepClone(v);
      }
      snapshot.storeState = serializable;
    }
  }

  // 2. Capture dragging node data
  const draggingNode = useCanvasStore.getState().draggingNodes.find(n => n.id === componentId);
  if (draggingNode?.data) {
    snapshot.draggingNodeData = deepClone(draggingNode.data);
  }

  // 3. Capture useProjectStore component-editing slice
  const ps = useProjectStore.getState();
  snapshot.projectStoreSlice = {
    componentName: ps.componentName,
    componentDescription: ps.componentDescription,
    metadataEnabled: ps.metadataEnabled,
    startTimestampLabel: ps.startTimestampLabel,
    endTimestampLabel: ps.endTimestampLabel,
    metadataTimestampFormat: ps.metadataTimestampFormat,
  };

  // 4. Capture the matching entry in useProjectStore.components (mutated by updateCanvasComponentInstruction)
  const compEntry = ps.components?.find(c => c.componentId === componentId);
  if (compEntry) {
    snapshot.projectComponentEntry = deepClone(compEntry);
  }

  return snapshot;
}

function restoreSnapshot(snapshot) {
  if (!snapshot) return false;

  const { componentId, componentType } = snapshot;

  // 1. Restore keyed store / form container
  if (FORM_TYPES.includes(componentType) && snapshot.formContainerState) {
    const container = getFormContainer(componentType);
    if (container) {
      const state = snapshot.formContainerState;
      if (componentType === 'form') {
        container.pages = deepClone(state.pages);
        container.pageTitles = deepClone(state.pageTitles);
      } else {
        for (const key of SEQUENTIAL_FORM_SNAPSHOT_KEYS) {
          if (key in state) container[key] = deepClone(state[key]);
        }
      }
      container.editTarget = '';
      container.onEdit = false;
      container.notify();
    }
  } else if (componentType === 'api_request_and_response' && snapshot.storeState) {
    const store = getAPIResReqStore(componentId);
    store.setState(deepClone(snapshot.storeState));
  } else if (snapshot.storeState) {
    const config = storeMap[componentType];
    if (config) {
      const store = config.getStore(componentId);
      store.setState(deepClone(snapshot.storeState));
    }
  }

  // 2. Restore dragging node data
  if (snapshot.draggingNodeData) {
    const restoredData = deepClone(snapshot.draggingNodeData);
    const updatedNodes = useCanvasStore.getState().draggingNodes.map(node => {
      if (node.id === componentId) {
        return { ...node, data: restoredData };
      }
      return node;
    });
    useCanvasStore.getState().setDraggingNodes(updatedNodes);
  }

  // 3. Restore useProjectStore slice
  if (snapshot.projectStoreSlice) {
    useProjectStore.setState(deepClone(snapshot.projectStoreSlice));
  }

  // 4. Restore the entry in useProjectStore.components
  if (snapshot.projectComponentEntry) {
    const components = useProjectStore.getState().components;
    if (components) {
      const idx = components.findIndex(c => c.componentId === componentId);
      if (idx >= 0) {
        components[idx] = deepClone(snapshot.projectComponentEntry);
      }
    }
  }

  return true;
}

const ComponentPane = React.forwardRef((props, ref) => {
    // ================================================================= State =========================================

    const {
      savingInfo,
      canvasViewport,
      canvasInstance,
      allPlaceholdersInWorkflow,
      placeholderMapSource,
      globalPlaceholders,
      credentialPlaceholders,
    } = useContext(CanvasContext);

    // Subscribe so the header re-renders when name/description change (enables editing the fields)
    const componentName = useProjectStore(s => s.componentName);
    const componentDescription = useProjectStore(s => s.componentDescription);
    const [saving, setSaving] = savingInfo ? savingInfo : useState({});
    const [instruction, setInstruction] = useState(
      props.editComponent && props.editComponent.description ? props.editComponent.description : ''
    );
    const [instructions, setInstructions] = useState(
      props.editComponent && props.editComponent.instructions
        ? props.editComponent.instructions
        : null
    );
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    // ================================================================= CSS =========================================

    const classes = props.classes;
    const navigate = useNavigate();

    // ================================================================= COMPONENT INFO =============================

    const [element, setElement] = useState(
      <IndividualComponentLoader
        componentType={props.editComponent?.type}
        componentName={props.editComponent?.label}
        componentDescription={props.editComponent?.description}
      />
    ); //react component to be rendered in the pane (or loading)
    const [resolvedPlaceholders, setResolvedPlaceholders] = useState(null);

    // ================================================================= PANE UI ====================================

    const [open, setOpen] = useState(false); // controls wether pane is open or closed
    const [loading, setLoading] = useState(false); //to disable buttons
    const [error, setError] = useState(false); //for the errors
    const [templateLoaded, setTemplateLoaded] = useState(false); //for the template loading

    // ================================================================= Context Values ====================================

    const [openSettings, setOpenSettings] = useState(false);
    const [openFormLink, setOpenFormLink] = useState(false);
    const [openExport, setOpenExport] = useState(false);
    const [openFormEmbed, setOpenFormEmbed] = useState(false);
    const [openComponentSettings, setOpenComponentSettings] = useState(false);
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [isDynamicForm, setIsDynamicForm] = useState(false);

    // ================================================================= Component Metadata Settings ====================================

    const metadataEnabled = useProjectStore(s => s.metadataEnabled);
    const startTimestampLabel = useProjectStore(s => s.startTimestampLabel);
    const endTimestampLabel = useProjectStore(s => s.endTimestampLabel);
    const metadataTimestampFormat = useProjectStore(s => s.metadataTimestampFormat);

    // ================================================================ STORE STATE =========================================

    const [lastModified, setLastModified] = useState('');
    const [viewport, setViewport] = canvasViewport ? canvasViewport : useState({});
    const [reactFlowInstance, setReactFlowInstance] = canvasInstance ? canvasInstance : '';
    const [showChildren, setShowChildren] = useState(true); // For controlling when the child unmounts

    // **************************************************************** FUNCTIONS THAT INITIALIZE COMPONENT PANE ****************************************************************

    const prevEditComponentRef = useRef(null);
    const [elementComponentId, setElementComponentId] = useState(null);
    const savingRef = useRef(false);
    savingRef.current = saving;
    const initializingRef = useRef(false);

    // Snapshot for discard-without-reload
    const snapshotRef = useRef(null);
    const discardCounterRef = useRef(0);

    useEffect(() => {
      const handleEditComponentChange = async () => {
        if (!props) return;

        const prevComponent = prevEditComponentRef.current;
        if (props.editComponent) {
          if (prevComponent && prevComponent.componentId !== props.editComponent.componentId) {
            fireAndForgetSave(prevComponent);
          }

          runComponentInitialization();

          const params = new URLSearchParams();
          params.append('view', props.editComponent.componentId);
          navigate({ search: params.toString() });

          const xOffset = !props.addressError ? -100 : 200;
          const remainingCanvasSpaceAsDecimal = 0.6;
          handleFocusComponent(xOffset, remainingCanvasSpaceAsDecimal);
          setInstruction(props.editComponent.description);
          setInstructions(
            props.editComponent.instructions ? props.editComponent.instructions : null
          );

          setShowChildren(true);
        }

        if (props.editComponent === null) {
          cleanupAndClose();
        }

        prevEditComponentRef.current = props.editComponent;
      };
      handleEditComponentChange();
    }, [props.editComponent]);

    // Extract common cleanup and close operations
    const cleanupAndClose = async () => {
      setShowChildren(false);
      setElementComponentId(null);
      snapshotRef.current = null;
      props.setSaving(false);
      useProjectStore.getState().clearComponentState();
      handleClose();
    };

    useEffect(() => {
      if (!props || !props.openTestPanel || !props.editComponent)
        return;

      const xOffset = -100;
      const remainingCanvasSpaceAsDecimal = 0.3;

      handleFocusComponent(xOffset, remainingCanvasSpaceAsDecimal);
    }, [props.openTestPanel]);

    useEffect(() => {
      setTemplateLoaded(props.childTemplateLoaded);
    }, [props.childTemplateLoaded]);

    const handleFocusComponent = (xOffset, remainingCanvasSpaceAsDecimal) => {
      if (!props.editComponent) return;
      let nodes =
        props.mode === 'DEFAULT' || props.mode === 'SAVE_PAUSED' ? useCanvasStore.getState().nodes : props.nodes;

      if (!nodes) return;

      // Grab the component from the nodes
      const component = nodes.find(
        ele => ele.type === 'component' && ele.id === props.editComponent.componentId
      );
      if (!component) return;

      // Create new viewport object
      const newViewport = {
        ...component.position,
        zoom: viewport ? viewport.zoom : DEFAULT_ZOOM_FOR_FOCUSING_VIEWPORT,
      };

      setPositionInCanvasView(
        newViewport,
        xOffset,
        reactFlowInstance,
        true,
        props.projectId,
        remainingCanvasSpaceAsDecimal
      );
    };

    const runComponentInitialization = () => {
      const compId = props.editComponent.componentId;
      props.updatePauseSave(true);

      setElementComponentId(compId);

      const canvasNode = useCanvasStore.getState().draggingNodes.find(n => n.id === compId);
      const currentLabel = canvasNode?.data?.label ?? props.editComponent.label;
      const currentDescription = canvasNode?.data?.description ?? props.editComponent.description;

      const node = useCanvasStore.getState().nodes.find(n => n.id === compId);
      const nodeData = node?.data || props.editComponent;

      initializingRef.current = true;
      useProjectStore.getState().setComponentName(currentLabel);
      useProjectStore.getState().setComponentDescription(currentDescription);

      // Write name/description to the keyed store so saves read from one place.
      // Also ensure `loaded` is true so the template renders (the store may not
      // have been populated yet if the backend fetch failed or is still in-flight).
      const compType = props.editComponent?.type;
      if (compType) {
        const config = storeMap[compType];
        if (config?.getStore) {
          const keyedStore = config.getStore(compId);
          keyedStore.setState({ loaded: true, name: currentLabel, componentDescription: currentDescription });
        }
      }

      useProjectStore.getState().setMetadataSettings({
        metadataEnabled: nodeData.metadataEnabled || false,
        startTimestampLabel: nodeData.startTimestampLabel || '',
        endTimestampLabel: nodeData.endTimestampLabel || '',
        metadataTimestampFormat: nodeData.metadataTimestampFormat || 'yyyy-MM-dd HH:mm:ss',
      });

      // Keep the components array in sync so keyed stores pick up the latest name
      const components = useProjectStore.getState().components;
      if (components) {
        const comp = components.find(c => c.componentId === compId);
        if (comp && comp.componentData) {
          comp.componentData.name = currentLabel;
        }
      }

      // Compute placeholders synchronously from in-memory data – zero network calls.
      const computedPlaceholders = computePlaceholdersForComponent(
        compId,
        components,
        globalPlaceholders || [],
        credentialPlaceholders || [],
      );

      const canonicalPlaceholders = computePlaceholdersForComponentFromOutputMap(
        compId,
        components,
        allPlaceholdersInWorkflow || {},
        globalPlaceholders || [],
        credentialPlaceholders || [],
      );

      const placeholders =
        placeholderMapSource === 'backend-v2'
          ? mergePlaceholderResponses(canonicalPlaceholders, computedPlaceholders)
          : computedPlaceholders;

      setResolvedPlaceholders(placeholders);
      setElement(
        React.cloneElement(getTemplate(props.editComponent.type), {
          key: `${props.editComponent.componentId}-${discardCounterRef.current}`,
          component_id: props.editComponent.componentId,
          projectId: props.projectId,
          project_id: props.projectId,
          version: props.version,
          onComponentNameChanged: onComponentNameChanged,
          onComponentDescriptionChanged: onComponentDescriptionChanged,
          onBackProjectCanvas: onBackProjectCanvas,
          onLastModifiedChanged: onLastModifiedChanged,
          showSuccess: props.showSuccess,
          showError: showError,
          availablePlaceholders: placeholders,
          onPlaceholderSelected: onPlaceholderSelected,
          isExpanded: false,
          componentType: props.editComponent.type,
          componentName: currentLabel,
          componentDescription: currentDescription,
          fetchPlaceholders: () => placeholders,
          setChildTemplateLoaded: val => {
            if (val) {
              initializingRef.current = false;
              // Capture snapshot once the template signals it has finished loading.
              // At this point the keyed store (or form container) is fully populated,
              // so the snapshot represents the last-saved state.
              if (
                props.editComponent?.componentId &&
                (!snapshotRef.current || snapshotRef.current.componentId !== props.editComponent.componentId)
              ) {
                snapshotRef.current = captureSnapshot(
                  props.editComponent.componentId,
                  props.editComponent.type,
                );
              }
            }
            props.setChildTemplateLoaded(val);
          },
          handleClose: val => {
            handleClose(val);
          },
          handleExpand: () => {
            handleExpand();
          },
          handleCollapse: () => {
            handleCollapse();
          },
        })
      );
      setLoading(false);
      if (props && props.setIsInit) props.setIsInit(true);
    };

    // Re-resolve placeholders when the canonical map arrives after the pane is already open.
    useEffect(() => {
      if (!props.editComponent?.componentId) return;

      const compId = props.editComponent.componentId;
      const components = useProjectStore.getState().components;

      const computed = computePlaceholdersForComponent(
        compId,
        components,
        globalPlaceholders || [],
        credentialPlaceholders || [],
      );

      const canonical = computePlaceholdersForComponentFromOutputMap(
        compId,
        components,
        allPlaceholdersInWorkflow || {},
        globalPlaceholders || [],
        credentialPlaceholders || [],
      );

      const updated =
        placeholderMapSource === 'backend-v2'
          ? mergePlaceholderResponses(canonical, computed)
          : computed;

      setResolvedPlaceholders(updated);
      setElement(prev =>
        prev ? React.cloneElement(prev, { availablePlaceholders: updated, fetchPlaceholders: () => updated }) : prev
      );
    }, [placeholderMapSource, allPlaceholdersInWorkflow]);

    // **************************************************************** UTILS TO OPEN/CLOSE PANE PARTS ****************************************************************

    useEffect(() => {
      //detect and open the pane
      setOpen(props.open);
    }, [props.open]);

    const handleClose = () => {
      if (props && props.setOpenTestPanel) props.setOpenTestPanel(false);

      setElement(
        <IndividualComponentLoader
          componentType={props.editComponent?.type}
          componentName={props.editComponent?.label}
          componentDescription={props.editComponent?.description}
        />
      );

      props.close(); //shut panel
    };

    // **************************************************************** UTILS TO HANDLE COMPONENT ***********************************************************************
    const onComponentNameChanged = newComponentName => {
      if (initializingRef.current) return;
      useProjectStore.getState().setComponentName(newComponentName);

      // Also write to the keyed store so saves read from one place
      const compType = props.editComponent?.type;
      const compId = props.editComponent?.componentId;
      if (compType && compId) {
        const config = storeMap[compType];
        if (config?.getStore) {
          const keyedStore = config.getStore(compId);
          keyedStore.setState({ name: newComponentName });
        }
      }

      const updatedNodes = useCanvasStore.getState().draggingNodes.map(component => {
        if (component.id == props.editComponent.componentId) {
          return {
            ...component,
            data: {
              ...component.data,
              label: newComponentName,
            },
          };
        }
        return component;
      });

      useCanvasStore.getState().setDraggingNodes(updatedNodes);
    };

    const onComponentDescriptionChanged = newDescription => {
      if (initializingRef.current) return;
      useProjectStore.getState().setComponentDescription(newDescription);

      // Also write to the keyed store so saves read from one place
      const compType = props.editComponent?.type;
      const compId = props.editComponent?.componentId;
      if (compType && compId) {
        const config = storeMap[compType];
        if (config?.getStore) {
          const keyedStore = config.getStore(compId);
          keyedStore.setState({ componentDescription: newDescription });
        }
      }

      const updatedNodes = useCanvasStore.getState().draggingNodes.map(component => {
        if (component.id == props.editComponent.componentId) {
          return {
            ...component,
            data: {
              ...component.data,
              description: newDescription,
            },
          };
        }
        return component;
      });

      useCanvasStore.getState().setDraggingNodes(updatedNodes);
    };

    const onLastModifiedChanged = lastModified => {
      //record last modified changes
      setLastModified(lastModified);
    };

    const showSuccess = () => {
      //show snackbar on save
    };

    const updateCanvasComponentInstruction = newInstruction => {
      try {
        let currId = props.editComponent.componentId;

        let ele = useCanvasStore.getState().draggingNodes.find(ele => ele.id === currId);
        if (!ele) return;

        ele.data.description = newInstruction;

        useProjectStore.getState().components.map((component, index) => {
          if (component.componentId == currId) {
            component.description = newInstruction;
          }
        });
      } catch (e) {
        console.log(e);
      }
    };
    const updateCanvasComponentInstructions = (newInstruction, isEdit) => {
      try {
        let currId = props.editComponent.componentId;

        let ele = useCanvasStore.getState().draggingNodes.find(ele => ele.id === currId);
        if (!ele) return;

        if (ele.data && ele.data.instructions) {
          if (isEdit) ele.data.instructions.editInstruction = newInstruction;
          else ele.data.instructions.buildInstruction = newInstruction;
        }

        useProjectStore.getState().components.map((component, index) => {
          if (component.componentId == currId) {
            component.instruction = newInstruction;
          }
        });
      } catch (e) {
        console.log(e);
      }
    };

    const showError = () => {
      //TODO handle errors
      setError(true);
    };

    const fireAndForgetSave = (prevComponent) => {
      if (props.production || useProjectStore.getState().production) {
        return;
      }
      const comp = prevComponent || props.editComponent;
      if (!comp) return;

      useCanvasStore.getState().syncNodes();

      saveComponentByType(comp.type, comp.componentId)
        .then(() => {
          props.showSuccess?.({}, {});
          useProjectStore.setState({ savedComponent: true });
          snapshotRef.current = captureSnapshot(comp.componentId, comp.type);
        })
        .catch(() => {});
    };

    const handleSave = async (skipEditCompCheck) => {
      const isSaving = savingRef.current;

      if (isSaving) {
        return;
      }

      if (
        !props.production &&
        (skipEditCompCheck || (props.editComponent && !props.editComponent.fromWorkflowPath))
      ) {
        if (!useProjectStore.getState().production) {
          const comp = props.editComponent;
          if (!comp) {
            return;
          }

          props.setSaving(true);
          useCanvasStore.getState().syncNodes();

          try {
            await saveComponentByType(comp.type, comp.componentId);
            useProjectStore.setState({ savedComponent: true });
            // Update snapshot baseline so discard rolls back to this saved state
            snapshotRef.current = captureSnapshot(comp.componentId, comp.type);
          } catch (err) {
            // save failed
          } finally {
            props.setSaving(false);
          }
        }
      }
    };

    const handleUpdateComponent = async (
      discardBuildInstruction = false,
      discardEditInstructions = false
    ) => {
      updateCanvasComponentInstruction(instruction);
      if (discardBuildInstruction) updateCanvasComponentInstructions(null, false);
      if (discardEditInstructions) updateCanvasComponentInstructions(null, true);
      useCanvasStore.getState().syncNodes();
      handleSave();
    };

    // Save metadata settings: update stores + canvas node data, then trigger template save
    const handleSaveMetadataSettings = (settings) => {
      if (!props.editComponent?.componentId) return;

      // Update project store so the template save payload picks up these values
      useProjectStore.getState().setMetadataSettings({
        metadataEnabled: settings.metadataEnabled,
        startTimestampLabel: settings.startTimestampLabel,
        endTimestampLabel: settings.endTimestampLabel,
        metadataTimestampFormat: settings.metadataTimestampFormat,
      });

      // Update canvas node data for in-session display
      useCanvasStore.getState().setNodesFn(nodes =>
        nodes.map(node => {
          if (node.id === props.editComponent.componentId) {
            return {
              ...node,
              data: {
                ...node.data,
                metadataEnabled: settings.metadataEnabled,
                startTimestampLabel: settings.startTimestampLabel,
                endTimestampLabel: settings.endTimestampLabel,
                metadataTimestampFormat: settings.metadataTimestampFormat,
              },
            };
          }
          return node;
        })
      );

      handleSave();
    };

    const handleDiscard = () => {
      const snap = snapshotRef.current;
      if (!snap || !props.editComponent) {
        window.location.reload();
        return;
      }

      const restored = restoreSnapshot(snap);
      if (!restored) {
        window.location.reload();
        return;
      }

      // Force re-mount of the template component so it picks up the restored store state.
      // We use a ref-based counter to avoid stale closure issues with React batching.
      discardCounterRef.current += 1;
      initializingRef.current = true;
      runComponentInitialization();
    };

    //keep these methods so the templates don't crash
    const onBackProjectCanvas = () => {};

    const onPlaceholderSelected = placeholderUpdateEvent => {
      useProjectStore.getState().setSelectedPlaceholder({ selected: placeholderUpdateEvent });
    };

    const isForm = () => {
      return props.editComponent && isLegacyForm(props.editComponent.type);
    };

    // Function to calculate the width of the pane
    const calculatePaneWidth = () => {
      if (isForm()) {
        return config.FORM_PANE_WIDTH;
      }
      return config.PANE_WIDTH;
    };

    const isPanelOpen = () => props.openTestPanel;

    // Function to calculate the translation percentage based on the state of the panels and form
    const calculateTranslationPercentage = () => {
      if (props.openTestPanel) {
        return isForm()
          ? config.TRANSLATION_ONLY_TEST_PANEL_OPEN_FORM
          : config.TRANSLATION_ONLY_TEST_PANEL_OPEN_NOT_FORM;
      }

      return '';
    };
    const paneWidth = calculatePaneWidth();
    const translationPercentage = calculateTranslationPercentage();

    const handleFormPreview = async () => {
      const comp = props.editComponent;
      if (!comp) return;
      const { componentId } = comp;
      if (!useProjectStore.getState().production) {
        await saveComponentByType(comp.type, componentId);

        send_request(`project-service/project/last-version/${props.projectId}`, '', '', 'get').then(
          res => {
            if (res && res.data) {
              let { draftVersion } = res.data;
              const url = config.buildFormUrl(`/form/form_preview/${componentId}/${draftVersion}`);
              setPreviewUrl(url);
              setPreviewModalOpen(true);
            }
          }
        );
      }
    };

    const isFormComponent = () => {
      return props.editComponent && props.editComponent.type === 'form';
    };

    return (
      <FormContext.Provider
        value={{
          settings: [openSettings, setOpenSettings],
          formLink: [openFormLink, setOpenFormLink],
          formExport: [openExport, setOpenExport],
          formEmbed: [openFormEmbed, setOpenFormEmbed],
          componentSettings: [openComponentSettings, setOpenComponentSettings],
          styling: [openUploadDialog, setOpenUploadDialog],
          placeholders: [isDynamicForm, setIsDynamicForm],
        }}
      >
        {showChildren && (
          <Drawer
            open={props.open}
            anchor={'right'}
            hideBackdrop={true}
            variant={'permanent'}
            onClose={handleClose}
            PaperProps={{
              sx: {
                width: paneWidth,
                backgroundColor: 'background.default',
                height: '100%',
                maxHeight: '100%',
                zIndex: 1001,
                transform: isPanelOpen() ? translationPercentage : '',
                boxShadow: 'none',
              },
            }}
          >
            {useProjectStore.getState().production && (
              <ReadOnlyBanner>
                This component is read only and changes will not be saved
              </ReadOnlyBanner>
            )}
            <TemplateContainer>
              <ComponentPaneHeader
                production={props.production}
                setOpenTestPanel={props.setOpenTestPanel}
                setTestFromCompPanel={props.setTestFromCompPanel}
                loading={loading}
                saving={saving}
                lastUpdated={lastModified}
                type={props.editComponent ? props.editComponent.type : ''}
                name={componentName}
                componentDescription={componentDescription}
                updateComponentDescription={onComponentDescriptionChanged}
                componentId={props.editComponent ? props.editComponent.componentId : ''}
                handleClose={props.handleClosePaneButtonClick}
                updateName={onComponentNameChanged}
                save={handleSave}
                onDiscard={handleDiscard}
                buildInstruction={instruction}
                instructions={instructions}
                setInstructions={setInstructions}
                setBuildInstruction={setInstruction}
                saveBuildInstruction={handleUpdateComponent}
                setSidebarState={props.setSidebarState}
                templateLoaded={!templateLoaded}
                additionalButtons={
                  isForm() ? (
                    <FormAdditionalButtons
                      templateLoaded={!templateLoaded}
                      isForm={isFormComponent() ? true : false}
                      handleSettings={() => {
                        setOpenSettings(!openSettings);
                      }}
                      handlePreview={handleFormPreview}
                      handleLink={() => setOpenFormLink(!openFormLink)}
                      handleData={() => setOpenExport(!openExport)}
                      handleEmbed={() => setOpenFormEmbed(!openFormEmbed)}
                      handleFormSettings={() => setOpenComponentSettings(!openComponentSettings)}
                      handleStyling={() => setOpenUploadDialog(!openUploadDialog)}
                      isDynamicForm={isDynamicForm}
                      handleEnablePlaceholders={event => {
                        setIsDynamicForm(event.target.checked);
                      }}
                    />
                  ) : (
                    <Grid item>
                      <TextButton
                        size="small"
                        startIcon={<Settings />}
                        onClick={() => setOpenComponentSettings(true)}
                        disabled={!templateLoaded}
                      >
                        Settings
                      </TextButton>
                    </Grid>
                  )
                }
                baseColor={'#7ac0f8'}
              />

              {showChildren && element && elementComponentId === props.editComponent?.componentId
                ? element
                : showChildren && props.editComponent
                  ? <IndividualComponentLoader
                      componentType={props.editComponent?.type}
                      componentName={props.editComponent?.label}
                      componentDescription={props.editComponent?.description}
                    />
                  : null}
            </TemplateContainer>
          </Drawer>
        )}
        <FormPreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          previewUrl={previewUrl}
        />
        {/* Component Metadata Settings Dialog for non-form components */}
        {!isForm() && (
          <ComponentMetadataSettingsDialog
            open={openComponentSettings}
            onClose={() => setOpenComponentSettings(false)}
            componentId={props.editComponent?.componentId || ''}
            metadataEnabled={metadataEnabled}
            startTimestampLabel={startTimestampLabel}
            endTimestampLabel={endTimestampLabel}
            metadataTimestampFormat={metadataTimestampFormat}
            onSave={handleSaveMetadataSettings}
          />
        )}
      </FormContext.Provider>
    );
});

export default DragDropContext(HTML5Backend)(withStyles(styles)(ComponentPane));

ComponentPane.propTypes = {
  open: PropTypes.bool.isRequired,
  editComponent: PropTypes.object,
  production: PropTypes.bool,
  updateCanvas: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  setIsCanvasLocked: PropTypes.func,
};
